import { FormEvent, useEffect, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Bot, Building2, Loader2, Lock } from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  fetchBusinessDetails,
  updateBusinessDetails,
  updateBusinessSettings,
  type BusinessDetails,
} from "../../services/apiService";
import { useBusiness } from "../../contexts/BusinessContext";
import { INDUSTRIES } from "../../data/categories";

const NAVY = "#2a2420";
const ORANGE = "#607840";
const SURFACE = "#fff";
const RAISED = "#fff";
const CANVAS = "#fff";
const LINE = "#e9e1d4";
const MUTED = "#8a8074";
const CHIP = "#f3ece1";
const SHADOW = "0 1px 3px rgba(42,36,32,.08), 0 12px 28px rgba(42,36,32,.06)";
const ACCENT_WEAK = "rgba(96,120,64,0.12)";
const ACCENT_LINE = "rgba(96,120,64,0.30)";
const ACCENT_SHADOW = "0 2px 8px rgba(96,120,64,0.25)";
const UP = "#c2412e";
const UP_WEAK = "rgba(194,65,46,0.12)";
const UP_LINE = "rgba(194,65,46,0.24)";
const DOWN = "#3c7d57";
const DOWN_WEAK = "rgba(60,125,87,0.12)";
const DOWN_LINE = "rgba(60,125,87,0.24)";

type BusinessForm = {
  name: string;
  industry: string;
  abn: string;
  phone: string;
  email: string;
  address: string;
};

const EMPTY_FORM: BusinessForm = {
  name: "",
  industry: "",
  abn: "",
  phone: "",
  email: "",
  address: "",
};

function toForm(details?: BusinessDetails): BusinessForm {
  return {
    name: details?.name ?? "",
    industry: details?.industry ?? "",
    abn: details?.abn ?? "",
    phone: details?.phone ?? "",
    email: details?.email ?? "",
    address: details?.address ?? "",
  };
}

function Field({
  label,
  value,
  onChange,
  disabled,
  type = "text",
  required = false,
  children,
}: {
  label: string;
  value?: string;
  onChange?: (value: string) => void;
  disabled?: boolean;
  type?: "text" | "email" | "tel";
  required?: boolean;
  children?: ReactNode;
}) {
  return (
    <label className="space-y-1.5">
      <span className="text-xs font-semibold" style={{ color: MUTED }}>
        {label}{required && <span style={{ color: ORANGE }}> *</span>}
      </span>
      {children ?? (
        <input
          type={type}
          required={required}
          disabled={disabled}
          value={value}
          onChange={(event) => onChange?.(event.target.value)}
          className="w-full rounded-lg px-3 py-2.5 text-sm outline-none transition-all disabled:opacity-60"
          style={{ border: `1.5px solid ${LINE}`, color: NAVY, backgroundColor: disabled ? CHIP : SURFACE }}
        />
      )}
    </label>
  );
}

export default function BusinessSettingsPage({ embedded = false }: { embedded?: boolean }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { activeBusiness, refreshMemberships, setActiveBusiness } = useBusiness();
  const [form, setForm] = useState<BusinessForm>(EMPTY_FORM);
  const [message, setMessage] = useState("");

  const businessQuery = useQuery({
    queryKey: ["business-details", activeBusiness?.id],
    queryFn: () => fetchBusinessDetails(activeBusiness?.id),
    enabled: Boolean(activeBusiness),
  });

  const isOwner = businessQuery.data?.role === "owner" || activeBusiness?.role === "owner";

  useEffect(() => {
    if (businessQuery.data) {
      setForm(toForm(businessQuery.data));
    }
  }, [businessQuery.data]);

  const saveMutation = useMutation({
    mutationFn: () => updateBusinessDetails(form, activeBusiness?.id),
    onSuccess: async (updated) => {
      setMessage("Business details updated.");
      queryClient.setQueryData(["business-details", activeBusiness?.id], updated);
      if (activeBusiness) {
        setActiveBusiness({ ...activeBusiness, name: updated.name });
      }
      await refreshMemberships();
    },
    onError: (error) => {
      setMessage(error instanceof Error ? error.message : "Could not update business details.");
    },
  });

  function update<K extends keyof BusinessForm>(key: K, value: BusinessForm[K]) {
    setMessage("");
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (!isOwner) return;
    if (!form.name.trim()) {
      setMessage("Business name is required.");
      return;
    }
    saveMutation.mutate();
  }

  return (
    <div
      className={embedded ? "p-6" : "min-h-full p-6"}
      style={{
        backgroundColor: embedded ? "transparent" : CANVAS,
        fontFamily: "var(--font-ui)",
      }}
    >
      <div className="max-w-6xl mx-auto">
        {!embedded && (
          <div className="mb-8">
            <button
              type="button"
              onClick={() => navigate("/console-settings")}
              className="flex items-center gap-2 text-sm mb-5 transition-opacity hover:opacity-70"
              style={{ color: NAVY, opacity: 0.55 }}
            >
              <ArrowLeft size={16} />
              Back to settings
            </button>
            <div className="flex items-start gap-4">
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: ACCENT_WEAK, color: ORANGE, border: `1px solid ${ACCENT_LINE}` }}
              >
                <Building2 size={22} />
              </div>
              <div>
                <h1 className="text-[27px] font-semibold leading-tight" style={{ color: NAVY }}>Business Settings</h1>
                <p className="text-sm mt-1" style={{ color: MUTED }}>
                  Manage details, contact information, and workspace-level settings for {activeBusiness?.name ?? "this business"}.
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="max-w-5xl space-y-6">
        <div
          className="rounded-xl overflow-hidden"
          style={{ backgroundColor: SURFACE, boxShadow: SHADOW, border: `1px solid ${LINE}` }}
        >
          <div className="px-5 py-4 flex items-center justify-between" style={{ backgroundColor: RAISED, borderBottom: `1px solid ${LINE}` }}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center">
                <Building2 size={18} style={{ color: ORANGE }} />
              </div>
              <div>
                <p className="text-sm font-bold" style={{ color: NAVY }}>Business details</p>
                <p className="text-xs mt-0.5" style={{ color: MUTED }}>
                  {isOwner ? "Owners can edit these fields." : "Only owners can edit these fields."}
                </p>
              </div>
            </div>
            {!isOwner && (
              <span className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-bold" style={{ color: NAVY, backgroundColor: CHIP, border: `1px solid ${LINE}` }}>
                <Lock size={12} />
                Read only
              </span>
            )}
          </div>

          {businessQuery.isLoading ? (
            <div className="p-10 flex items-center justify-center">
              <Loader2 size={20} className="animate-spin" style={{ color: ORANGE }} />
            </div>
          ) : businessQuery.error ? (
            <div className="p-5 text-sm" style={{ color: UP }}>
              Could not load business details: {(businessQuery.error as Error).message}
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="p-5 space-y-4">
              <Field label="Business name" required value={form.name} disabled={!isOwner} onChange={(value) => update("name", value)} />

              <Field label="Industry" disabled={!isOwner}>
                <select
                  value={form.industry}
                  disabled={!isOwner}
                  onChange={(event) => update("industry", event.target.value)}
                  className="w-full rounded-lg px-3 py-2.5 text-sm outline-none transition-all disabled:opacity-60"
                  style={{ border: `1.5px solid ${LINE}`, color: NAVY, backgroundColor: !isOwner ? CHIP : SURFACE }}
                >
                  <option value="">Select industry</option>
                  {INDUSTRIES.map((industry) => (
                    <option key={industry} value={industry}>{industry}</option>
                  ))}
                </select>
              </Field>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="ABN" value={form.abn} disabled={!isOwner} onChange={(value) => update("abn", value)} />
                <Field label="Phone" type="tel" value={form.phone} disabled={!isOwner} onChange={(value) => update("phone", value)} />
                <Field label="Email" type="email" value={form.email} disabled={!isOwner} onChange={(value) => update("email", value)} />
                <Field label="Address" value={form.address} disabled={!isOwner} onChange={(value) => update("address", value)} />
              </div>

              {message && (
                <p
                  className="text-xs px-3 py-2 rounded-lg"
                  style={{
                    color: message.includes("updated") ? DOWN : UP,
                    backgroundColor: message.includes("updated") ? DOWN_WEAK : UP_WEAK,
                    border: `1px solid ${message.includes("updated") ? DOWN_LINE : UP_LINE}`,
                  }}
                >
                  {message}
                </p>
              )}

              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={!isOwner || saveMutation.isPending}
                  className="inline-flex items-center gap-2 rounded-lg px-5 py-2.5 text-sm font-bold text-white transition-all disabled:opacity-50"
                  style={{ backgroundColor: ORANGE, boxShadow: ACCENT_SHADOW }}
                >
                  {saveMutation.isPending && <Loader2 size={14} className="animate-spin" />}
                  Save changes
                </button>
              </div>
            </form>
          )}
        </div>

        <AiSettingsCard
          isOwner={isOwner}
          businessId={activeBusiness?.id}
          initialAutoCorrect={Boolean(businessQuery.data?.settings?.ai_auto_correct)}
          queryClient={queryClient}
        />
        </div>
      </div>
    </div>
  );
}

function AiSettingsCard({
  isOwner,
  businessId,
  initialAutoCorrect,
  queryClient,
}: {
  isOwner: boolean;
  businessId?: string;
  initialAutoCorrect: boolean;
  queryClient: ReturnType<typeof useQueryClient>;
}) {
  const [autoCorrect, setAutoCorrect] = useState(initialAutoCorrect);

  useEffect(() => { setAutoCorrect(initialAutoCorrect); }, [initialAutoCorrect]);

  const mutation = useMutation({
    mutationFn: (value: boolean) => updateBusinessSettings({ ai_auto_correct: value }, businessId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["business-details", businessId] });
    },
  });

  function toggle() {
    if (!isOwner) return;
    const next = !autoCorrect;
    setAutoCorrect(next);
    mutation.mutate(next);
  }

  return (
    <div
      className="rounded-xl overflow-hidden"
      style={{ backgroundColor: SURFACE, boxShadow: SHADOW, border: `1px solid ${LINE}` }}
    >
      <div className="px-5 py-4 flex items-center justify-between" style={{ backgroundColor: RAISED, borderBottom: `1px solid ${LINE}` }}>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 flex items-center justify-center">
            <Bot size={18} style={{ color: ORANGE }} />
          </div>
          <div>
            <p className="text-sm font-bold" style={{ color: NAVY }}>AI & Automation</p>
            <p className="text-xs mt-0.5" style={{ color: MUTED }}>
              Control AI-powered features across your workspace.
            </p>
          </div>
        </div>
        {!isOwner && (
          <span className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-bold" style={{ color: NAVY, backgroundColor: CHIP, border: `1px solid ${LINE}` }}>
            <Lock size={12} />
            Read only
          </span>
        )}
      </div>

      <div className="p-5">
        <div className="flex items-center justify-between gap-4 py-2">
          <div>
            <p className="text-sm font-semibold" style={{ color: NAVY }}>Auto-correct high-confidence scan errors</p>
            <p className="text-xs mt-0.5" style={{ color: MUTED }}>
              When enabled, product codes identified as mis-scans with high confidence are corrected automatically. Otherwise you'll be prompted to confirm each suggestion.
            </p>
          </div>
          <button
            type="button"
            onClick={toggle}
            disabled={!isOwner || mutation.isPending}
            className="relative flex-shrink-0 w-11 h-6 rounded-full transition-colors duration-200 disabled:opacity-50"
            style={{
              backgroundColor: autoCorrect ? ORANGE : CHIP,
              boxShadow: `inset 0 0 0 1px ${autoCorrect ? ACCENT_LINE : LINE}`,
            }}
            aria-label="Toggle auto-correct"
          >
            <span
              className="absolute top-0.5 left-0.5 w-5 h-5 rounded-full transition-transform duration-200"
              style={{
                backgroundColor: SURFACE,
                boxShadow: "0 1px 4px rgba(80,55,30,0.18)",
                transform: autoCorrect ? "translateX(20px)" : "translateX(0)",
              }}
            />
          </button>
        </div>
      </div>
    </div>
  );
}
